import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { RouterProvider } from 'react-router-dom'
import * as Sentry from '@sentry/react'
import { TooltipProvider } from '@/components/ui/tooltip'
import { AuthProvider } from '@/hooks/AuthProvider'
import { ThemeProvider } from '@/hooks/ThemeProvider'
import { ThemeAwareToaster } from '@/components/ThemeAwareToaster'
import { router } from './router'
import './index.css'

const sentryDsn = import.meta.env.VITE_SENTRY_DSN
if (sentryDsn) {
  Sentry.init({
    dsn: sentryDsn,
    environment: import.meta.env.MODE,
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({ maskAllText: false, blockAllMedia: false }),
    ],
    tracesSampleRate: import.meta.env.PROD ? 0.1 : 0,
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: import.meta.env.PROD ? 1.0 : 0,
  })
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000,
      gcTime: 10 * 60 * 1000,
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
})

const app = (
  <QueryClientProvider client={queryClient}>
    <ThemeProvider>
      <AuthProvider>
        <TooltipProvider>
          <RouterProvider router={router} />
        </TooltipProvider>
        <ThemeAwareToaster />
      </AuthProvider>
    </ThemeProvider>
  </QueryClientProvider>
)

const rootElement = document.getElementById('root')!
const renderTree = sentryDsn ? (
  <Sentry.ErrorBoundary fallback={<p className="p-4 text-destructive">An error occurred. Please reload.</p>}>
    {app}
  </Sentry.ErrorBoundary>
) : app

if (import.meta.env.DEV) {
  createRoot(rootElement).render(<StrictMode>{renderTree}</StrictMode>)
} else {
  createRoot(rootElement).render(renderTree)
}
